import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, ArrowRight, Sparkles, ShieldCheck, BadgeCheck, Briefcase,
  Clock, CheckCircle2, ChevronRight, Building2, HeartPulse,
} from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import ShiftCard, { ShiftCardSkeleton } from "../../components/shifts/ShiftCard";
import EmptyState from "../../components/shifts/EmptyState";
import PageMeta from "../../components/seo/PageMeta";
import {
  parseSeoSlug, buildSeoMeta, filterShifts,
  TOP_CITIES, SEO_SPECIALTIES,
} from "../../data/seoLandingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.tritalcare.com";

/* ------------------------------------------------------------------ */
/*  Dynamic SEO Landing — /shiftbridge/prn-shifts/:slug               */
/*                                                                    */
/*  ~340 unique long-tail-targeting pages from a single component.    */
/*  Each page emits scoped JobPosting + BreadcrumbList + FAQPage      */
/*  JSON-LD schemas to dominate "PRN shifts" + location/specialty     */
/*  Google searches.                                                  */
/* ------------------------------------------------------------------ */

export default function SeoLanding() {
  const { slug } = useParams();
  const parsed = useMemo(() => parseSeoSlug(slug), [slug]);
  const meta = useMemo(() => buildSeoMeta(parsed), [parsed]);

  const [allShifts, setAllShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  /* Pull a generous live shift slice; we'll filter client-side */
  useEffect(() => {
    if (parsed.type === "invalid") return;
    setLoading(true);
    fetch(`${API}/public/shifts?limit=60`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setAllShifts(Array.isArray(data) ? data : []))
      .catch(() => setAllShifts([]))
      .finally(() => setLoading(false));
  }, [parsed.type]);

  const matching = useMemo(() => filterShifts(allShifts, {
    city: parsed.type === "location" || parsed.type === "combo" ? parsed.city : null,
    specialty: parsed.type === "specialty" || parsed.type === "combo" ? parsed.specialty : null,
  }), [allShifts, parsed]);

  /* 404 — bad slug (after all hooks have been called to obey React rules) */
  if (parsed.type === "invalid") {
    return <Navigate to="/shiftbridge/prn-shifts" replace />;
  }

  const url = `${SITE}/shiftbridge/prn-shifts/${slug}`;
  const ogImage = `${SITE}/images/logos/trital-shiftbridge-logo-transparent-master.png`;

  /* ----- JSON-LD: BreadcrumbList + scoped EmploymentAgency + FAQPage + JobPosting list ----- */
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",         "item": `${SITE}/` },
        { "@type": "ListItem", "position": 2, "name": "ShiftBridge",  "item": `${SITE}/shiftbridge` },
        { "@type": "ListItem", "position": 3, "name": "PRN Shifts",   "item": `${SITE}/shiftbridge/prn-shifts` },
        { "@type": "ListItem", "position": 4, "name": meta.crumb,     "item": url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": ["EmploymentAgency", "MedicalBusiness"],
      "name": `TRITAL ShiftBridge™ — ${meta.crumb}`,
      "url": url,
      "logo": ogImage,
      "image": ogImage,
      "description": meta.description,
      "parentOrganization": { "@type": "Organization", "name": "TRITAL Care®", "url": SITE },
      "areaServed": parsed.type === "location" || parsed.type === "combo"
        ? { "@type": "City", "name": parsed.city.city, "containedInPlace": { "@type": "State", "name": parsed.city.state } }
        : { "@type": "Country", "name": "United States" },
      "telephone": "+1-832-344-1525",
      "email": "shifts@tritalcare.com",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": buildFaqs(parsed).map((q) => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": { "@type": "Answer", "text": q.a },
      })),
    },
  ];

  // Do not attach JobPosting structured data to category/location listing pages.
  // Add JobPosting only to individual verified, currently open job detail URLs.

  /* Cross-link siblings (internal SEO linking) */
  const relatedLinks = buildRelatedLinks(parsed);

  return (
    <ShiftsLayout>
      <PageMeta
        title={meta.title}
        description={meta.description}
        canonical={url}
        ogImage={ogImage}
        schema={schemas}
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-trital-mesh border-b border-slate-200" data-testid="seo-hero">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-12 pb-12 lg:pt-16 lg:pb-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mb-5" aria-label="Breadcrumb" data-testid="seo-breadcrumb">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <Link to="/shiftbridge" className="hover:text-trital-navy">ShiftBridge</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <Link to="/shiftbridge/prn-shifts" className="hover:text-trital-navy">PRN Shifts</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <span className="text-trital-navy font-semibold">{meta.crumb}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card mb-5">
            <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
            <span className="overline text-trital-navy">
              {parsed.type === "combo" ? "Specialty + Location" :
               parsed.type === "specialty" ? "Specialty page · Nationwide" :
               "Location page · All specialties"}
            </span>
          </div>

          <h1
            className="font-display font-black text-4xl lg:text-[58px] leading-[1.02] tracking-tight text-slate-900 text-balance max-w-5xl"
            data-testid="seo-h1"
          >
            {meta.h1}
          </h1>
          <p className="mt-5 text-slate-700 max-w-3xl text-lg leading-relaxed">
            {meta.description}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/shiftbridge/browse${buildBrowseQuery(parsed)}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="seo-cta-browse"
            >
              See all open shifts <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link
              to="/shiftbridge/signup/clinician"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold btn-lift hover:border-trital-navy"
              data-testid="seo-cta-signup"
            >
              Create free clinician account
            </Link>
          </div>

          {/* Stats strip */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl">
            {[
              { v: matching.length || "0", l: "Open shifts now" },
              { v: "W-2", l: "Every assignment" },
              { v: "$0", l: "Clinician fees" },
              { v: "<24h", l: "Avg approval" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-slate-200 p-4 shadow-card">
                <div className="font-display font-black text-2xl text-slate-900 tabular-nums">{s.v}</div>
                <div className="overline text-slate-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LIVE SHIFTS ============ */}
      <section className="bg-white border-b border-slate-200" data-testid="seo-shifts">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="overline text-trital-red">Live shifts matching this page</div>
              <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
                {loading ? "Loading open shifts…" : `${matching.length} matching open shift${matching.length === 1 ? "" : "s"}`}
              </h2>
              <p className="mt-2 text-slate-600 max-w-2xl text-sm">
                These are live W-2 PRN shifts from TRITAL ShiftBridge™ matching{" "}
                <span className="font-semibold text-slate-900">{meta.crumb}</span>. Claim in one tap once your credentials are approved.
              </p>
            </div>
            <Link
              to={`/shiftbridge/browse${buildBrowseQuery(parsed)}`}
              className="text-trital-navy text-[13px] font-semibold underline underline-offset-2 hover:text-trital-red inline-flex items-center gap-1"
              data-testid="seo-shifts-view-all"
            >
              View all in browse <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((i) => <ShiftCardSkeleton key={i} />)}
            </div>
          ) : matching.length === 0 ? (
            <EmptyState
              title="No open shifts matching right now."
              body="New shifts post hourly. Create a free clinician account and we'll alert you the moment a matching shift opens."
              ctaLabel="Create free account"
              ctaTo="/shiftbridge/signup/clinician"
              data-testid="seo-empty-state"
            />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {matching.slice(0, 12).map((s) => (
                <ShiftCard key={s.id} shift={s} variant="list" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ SEO CONTENT BLOCK (keyword-rich) ============ */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <div>
            <div className="overline text-trital-red">Why clinicians choose TRITAL ShiftBridge™</div>
            <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900 max-w-3xl text-balance">
              {buildContentTitle(parsed)}
            </h2>
            <div className="mt-6 prose prose-slate max-w-none text-slate-700 leading-relaxed">
              {buildContentBody(parsed).map((p, i) => <p key={i} className="mt-4 first:mt-0">{p}</p>)}
            </div>

            <ul className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { Icon: BadgeCheck,  title: "W-2 weekly pay",      sub: "Proper withholdings. No 1099 surprises at tax time." },
                { Icon: ShieldCheck, title: "Zero clinician fees", sub: "Posted rate is the rate. Always." },
                { Icon: Briefcase,   title: "Transparent rates",   sub: "See exact $/hr before you claim. No hidden math." },
                { Icon: Clock,       title: "Same-day fill",       sub: "Urgent shifts surface to vetted clinicians fast." },
              ].map((b) => (
                <li key={b.title} className="flex gap-3 rounded-2xl bg-white border border-slate-200 p-4 shadow-card">
                  <div className="w-10 h-10 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
                    <b.Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[15px] text-slate-900">{b.title}</div>
                    <div className="text-[13px] text-slate-600 mt-0.5 leading-relaxed">{b.sub}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Sticky context card */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden">
              <div className="bg-trital-navy text-white px-6 py-5">
                <div className="overline text-white/70">{parsed.type === "specialty" ? "Specialty" : parsed.type === "location" ? "Location" : "Specialty + Location"}</div>
                <div className="mt-1 font-display font-black text-xl">{meta.crumb}</div>
              </div>
              <div className="p-6 space-y-4 text-[13.5px]">
                {parsed.type !== "specialty" && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-trital-red shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                      <div className="font-semibold text-slate-900">{parsed.city.city}, {parsed.city.state}</div>
                      <div className="text-slate-500 text-[12.5px]">{parsed.city.region} · {parsed.city.population} metro</div>
                    </div>
                  </div>
                )}
                {parsed.type !== "location" && (
                  <div className="flex items-start gap-2">
                    <HeartPulse className="w-4 h-4 text-trital-red shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                      <div className="font-semibold text-slate-900">{parsed.specialty.full}</div>
                      <div className="text-slate-500 text-[12.5px]">{parsed.specialty.summary}</div>
                    </div>
                  </div>
                )}
                <div className="h-px bg-slate-200" />
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-trital-red shrink-0 mt-0.5" strokeWidth={1.8} />
                  <div className="text-slate-600">
                    Live shifts pulled from TRITAL ShiftBridge™ marketplace. Filter and claim in browse.
                  </div>
                </div>
                <Link
                  to={`/shiftbridge/browse${buildBrowseQuery(parsed)}`}
                  className="block text-center mt-3 px-4 py-3 rounded-full bg-trital-red text-white font-semibold hover:bg-trital-red-deep btn-lift"
                  data-testid="seo-aside-cta"
                >
                  Browse {meta.crumb}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="overline text-trital-red">Frequently asked</div>
          <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
            About {meta.crumb}
          </h2>
          <div className="mt-8 space-y-4">
            {buildFaqs(parsed).map((f, i) => (
              <details key={i} className="group rounded-2xl bg-slate-50 border border-slate-200 p-5" data-testid={`seo-faq-${i}`}>
                <summary className="cursor-pointer font-display font-bold text-slate-900 text-[15.5px] flex items-start justify-between gap-4">
                  {f.q}
                  <ChevronRight className="w-4 h-4 text-trital-red mt-1 shrink-0 group-open:rotate-90 transition-transform" strokeWidth={2} />
                </summary>
                <p className="mt-3 text-slate-700 leading-relaxed text-[14px]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ RELATED CROSS-LINKS ============ */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="overline text-trital-red">Related PRN searches</div>
          <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
            Explore related opportunities
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="seo-related">
            {relatedLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="group flex items-start gap-3 p-5 rounded-2xl bg-white border border-slate-200 hover:border-trital-navy hover:shadow-card-hover transition-all"
                data-testid={`seo-related-${l.to.split("/").pop()}`}
              >
                <ArrowRight className="w-4 h-4 text-trital-red mt-1 shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                <div>
                  <div className="font-display font-bold text-[14px] text-slate-900 group-hover:text-trital-navy">{l.label}</div>
                  {l.sub && <div className="text-[12px] text-slate-500 mt-0.5">{l.sub}</div>}
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/shiftbridge/prn-shifts"
              className="inline-flex items-center gap-1 text-trital-navy text-[13.5px] font-semibold underline underline-offset-2 hover:text-trital-red"
              data-testid="seo-back-to-hub"
            >
              See all PRN landing pages <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90 pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-10 pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <CheckCircle2 className="w-10 h-10 text-trital-red mx-auto" strokeWidth={1.5} />
          <h2 className="mt-5 font-display font-black text-3xl lg:text-5xl leading-[1.05] text-white text-balance">
            Ready to claim your next <span className="text-trital-red">{meta.crumb.toLowerCase()}</span>?
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">
            Create your free clinician account in under 3 minutes. Browse, filter and claim W-2 PRN shifts in one tap.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/shiftbridge/signup/clinician"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="seo-final-cta-signup"
            >
              Create free account <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link
              to={`/shiftbridge/browse${buildBrowseQuery(parsed)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white font-semibold backdrop-blur hover:bg-white/15"
              data-testid="seo-final-cta-browse"
            >
              Browse open shifts
            </Link>
          </div>
        </div>
      </section>
    </ShiftsLayout>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function buildBrowseQuery(parsed) {
  const params = new URLSearchParams();
  if (parsed.type === "location" || parsed.type === "combo") {
    params.set("state", parsed.city.state);
  }
  if (parsed.type === "specialty" || parsed.type === "combo") {
    params.set("specialty", parsed.specialty.apiMatch[0]);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

function buildContentTitle(parsed) {
  if (parsed.type === "specialty") return `Where ${parsed.specialty.full} PRN shifts come from on TRITAL ShiftBridge™`;
  if (parsed.type === "location")  return `Why ${parsed.city.city}, ${parsed.city.state} clinicians work with TRITAL ShiftBridge™`;
  return `Why ${parsed.specialty.name} clinicians in ${parsed.city.city} choose TRITAL ShiftBridge™`;
}

function buildContentBody(parsed) {
  if (parsed.type === "specialty") {
    const s = parsed.specialty;
    return [
      `${s.full} (${s.name}) clinicians are in constant demand across the United States — and the gap between open shifts and qualified, credentialed clinicians ready to work is exactly what TRITAL ShiftBridge™ was built to close. ${s.summary}`,
      `Every TRITAL ShiftBridge™ assignment is W-2. That means every ${s.name} PRN shift you claim through us carries proper withholdings, employer-side payroll taxes, and the clean year-end paperwork that 1099 platforms make a hassle. Posted rates are transparent — what you see is what you earn, with weekly direct deposit and zero platform fees deducted from your check.`,
      `Compliance is non-negotiable. Before a ${s.name} clinician can claim a shift on TRITAL ShiftBridge™, their license, certifications, BLS/ACLS/PALS where required, immunizations, and skills checklist are verified by the TRITAL Care® compliance team. The result: hospitals trust the clinicians who claim, and clinicians never lose a paycheck to last-minute documentation gaps.`,
      `Whether you want to pick up a single ${s.name} shift this weekend or build a 30-shift quarter, you're in control — browse, filter by state and rate, and claim in one tap once you're approved.`,
    ];
  }
  if (parsed.type === "location") {
    const c = parsed.city;
    return [
      `${c.city}, ${c.state} is one of the highest-demand healthcare-staffing markets in the ${c.region} region. With a metro population of ${c.population} and a dense network of acute-care hospitals, level-trauma centers, specialty clinics, ambulatory surgery centers, skilled nursing facilities, and home-health agencies, qualified clinicians who can pick up PRN shifts here are rarely without work.`,
      `TRITAL ShiftBridge™ is the W-2 PRN marketplace built and operated by TRITAL Care® — a healthcare-only workforce partner with operator DNA. We don't list 1099 gig shifts. Every PRN shift you claim in ${c.city} is W-2 with weekly direct deposit, transparent posted rates, and zero clinician fees deducted from your pay.`,
      `Clinicians working in ${c.city} use TRITAL ShiftBridge™ for one simple reason: when an urgent shift opens at a vetted facility, we get it to a credentialed clinician fast — usually within hours. No bidding wars. No surprise pay cuts. No surprise 1099 at tax time.`,
      `Browse open ${c.city} PRN shifts above, or create a free clinician account in 3 minutes and we'll text you the moment a shift matching your specialty and rate opens.`,
    ];
  }
  /* combo */
  const { specialty: s, city: c } = parsed;
  return [
    `${s.full} PRN shifts in ${c.city}, ${c.state} are among the most-searched and fastest-filled categories on TRITAL ShiftBridge™. ${c.region} hospitals and specialty centers post open ${s.name} requisitions throughout the week — and credentialed clinicians on the ShiftBridge marketplace see them first.`,
    `${s.summary} In ${c.city} specifically, ${s.name} clinicians benefit from a dense healthcare network that includes academic medical centers, level-trauma facilities, and high-volume community hospitals — meaning a wider variety of shift environments, schedules, and rates than smaller metros offer.`,
    `Every ${s.name} PRN shift on TRITAL ShiftBridge™ is W-2 with weekly direct deposit. There are zero clinician fees, transparent posted rates, and a credentialing process backed by the TRITAL Care® compliance team. That combination — speed, transparency, and trust — is why ${s.name} clinicians in ${c.city} keep coming back.`,
    `Browse live ${s.name} PRN shifts in ${c.city}, ${c.state} above, or create a free clinician account to get text alerts the moment a matching shift opens.`,
  ];
}

function buildFaqs(parsed) {
  const base = [
    {
      q: "How fast can I start picking up shifts?",
      a: "Most clinicians complete TRITAL ShiftBridge™ onboarding in 48–72 hours: account creation, document upload, skills checklist, and compliance review. Once approved, you can claim any matching open shift in one tap.",
    },
    {
      q: "Are TRITAL ShiftBridge shifts W-2 or 1099?",
      a: "Every TRITAL ShiftBridge™ assignment is W-2. You receive weekly direct deposit with proper withholdings and employer-side payroll taxes paid — no 1099 surprises at year-end.",
    },
    {
      q: "What fees do clinicians pay?",
      a: "Zero. TRITAL ShiftBridge™ does not deduct platform fees, application fees, or payout fees from clinician pay. The posted hourly rate is exactly what you earn.",
    },
  ];

  if (parsed.type === "specialty") {
    return [
      {
        q: `What ${parsed.specialty.name} PRN shifts are available on TRITAL ShiftBridge™?`,
        a: `TRITAL ShiftBridge™ regularly lists ${parsed.specialty.full} PRN shifts across hospitals and SNFs nationwide. ${parsed.specialty.summary} Filter by state, shift type and rate in browse to see live openings.`,
      },
      ...base,
      {
        q: `Do I need a specific certification to take ${parsed.specialty.name} shifts?`,
        a: `Yes — most ${parsed.specialty.full} shifts require an active state license, BLS at minimum, and specialty certifications relevant to ${parsed.specialty.name} (ACLS, PALS, NRP, TNCC, CPI, etc., depending on the unit). The shift posting will list exact requirements, and TRITAL Care® compliance verifies them before you can claim.`,
      },
    ];
  }
  if (parsed.type === "location") {
    return [
      {
        q: `How many PRN shifts are open in ${parsed.city.city}, ${parsed.city.state}?`,
        a: `Open PRN shifts in ${parsed.city.city} change hourly. Browse this page or /shiftbridge/browse?state=${parsed.city.state} for the live count and current openings across all specialties.`,
      },
      ...base,
      {
        q: `What hospitals in ${parsed.city.city}, ${parsed.city.state} use TRITAL ShiftBridge™?`,
        a: `Facility names are visible to clinicians after credentialing approval to protect the privacy of TRITAL ShiftBridge™ hospital partners. The ${parsed.city.region} region includes a mix of academic, community, and specialty facilities — sign up to see the full list.`,
      },
    ];
  }
  return [
    {
      q: `How many ${parsed.specialty.name} PRN shifts are open in ${parsed.city.city}, ${parsed.city.state}?`,
      a: `Open ${parsed.specialty.full} PRN shifts in ${parsed.city.city} change hourly. Browse this page or filter at /shiftbridge/browse for the live count.`,
    },
    ...base,
    {
      q: `What are typical ${parsed.specialty.name} PRN rates in ${parsed.city.city}?`,
      a: `${parsed.specialty.name} PRN rates in ${parsed.city.city}, ${parsed.city.state} vary by facility, shift type (day/evening/night), and weekend/holiday differentials. TRITAL ShiftBridge™ posts the exact $/hr on every shift — what you see is what you earn.`,
    },
  ];
}

function buildRelatedLinks(parsed) {
  const links = [];
  if (parsed.type === "specialty") {
    /* Show top 4 cities for this specialty */
    TOP_CITIES.slice(0, 4).forEach((c) => {
      links.push({
        to: `/shiftbridge/prn-shifts/${parsed.specialty.slug}-${c.slug}`,
        label: `${parsed.specialty.name} PRN in ${c.city}, ${c.state}`,
        sub: c.region,
      });
    });
  } else if (parsed.type === "location") {
    /* Show top 4 specialties for this city */
    SEO_SPECIALTIES.slice(1, 5).forEach((s) => {
      links.push({
        to: `/shiftbridge/prn-shifts/${s.slug}-${parsed.city.slug}`,
        label: `${s.name} PRN in ${parsed.city.city}`,
        sub: s.full,
      });
    });
  } else {
    /* combo — 2 nearby cities (same region → fallback same state → fallback nearest US) + 2 sibling specialties same city */
    const nearbyPool = [
      ...TOP_CITIES.filter((c) => c.region === parsed.city.region && c.slug !== parsed.city.slug),
      ...TOP_CITIES.filter((c) => c.state === parsed.city.state && c.region !== parsed.city.region),
      ...TOP_CITIES.filter((c) => c.state !== parsed.city.state && c.slug !== parsed.city.slug),
    ];
    /* dedupe by slug, preserving order priority */
    const seen = new Set();
    const nearby = nearbyPool.filter((c) => {
      if (seen.has(c.slug)) return false;
      seen.add(c.slug);
      return true;
    }).slice(0, 2);
    nearby.forEach((c) => links.push({
      to: `/shiftbridge/prn-shifts/${parsed.specialty.slug}-${c.slug}`,
      label: `${parsed.specialty.name} PRN in ${c.city}, ${c.state}`,
      sub: c.state === parsed.city.state ? `Same state · ${c.region}` : c.region,
    }));
    const otherSpec = SEO_SPECIALTIES.filter((s) => s.slug !== parsed.specialty.slug).slice(0, 2);
    otherSpec.forEach((s) => links.push({
      to: `/shiftbridge/prn-shifts/${s.slug}-${parsed.city.slug}`,
      label: `${s.name} PRN in ${parsed.city.city}`,
      sub: "Same city",
    }));
  }
  return links;
}
